import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Badge,
  Button,
  Callout,
  Card,
  Field,
  Input,
  Loading,
  PageHeader,
  Table,
  Td,
} from '../components/ui/primitives';
import type { ClientRecord } from '../lib/types';

export function ClientsPage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    physicalAddress: '',
    replyTo: '',
    timezone: 'Europe/London',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ clients: ClientRecord[] }>('/api/clients'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/clients', {
        name: form.name,
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        physicalAddress: form.physicalAddress || null,
        replyTo: form.replyTo || null,
        timezone: form.timezone,
      }),
    onSuccess: () => {
      setOpen(false);
      setForm({ name: '', slug: '', physicalAddress: '', replyTo: '', timezone: 'Europe/London' });
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (isLoading) return <Loading />;

  return (
    <>
      <PageHeader
        title="Clients"
        description="Each client owns its own contacts, suppression list and campaigns. Suppression never leaks between clients, but a complaint suppresses the address everywhere."
        actions={
          <Button variant="primary" onClick={() => setOpen((v) => !v)}>
            {open ? 'Cancel' : 'Add client'}
          </Button>
        }
      />

      {open && (
        <Card title="New client" className="mb-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Slug" hint="Lowercase identifier used in URLs">
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </Field>
            <Field
              label="Physical postal address"
              hint="Required in every commercial email footer and a strong trust signal for filters."
            >
              <Input
                value={form.physicalAddress}
                onChange={(e) => setForm({ ...form, physicalAddress: e.target.value })}
              />
            </Field>
            <Field label="Reply-to address">
              <Input value={form.replyTo} onChange={(e) => setForm({ ...form, replyTo: e.target.value })} />
            </Field>
            <Field label="Timezone" hint="Send windows are evaluated in this timezone.">
              <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
            </Field>
          </div>
          {error && (
            <div className="mt-4">
              <Callout tone="bad">{error}</Callout>
            </div>
          )}
          <div className="mt-4">
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
              Create client
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <Table head={['Client', 'Status', 'Contacts', 'Active', 'Timezone', 'Postal address']}>
          {(data?.clients ?? []).map((client) => (
            <tr key={client.id}>
              <Td>
                <div className="font-medium text-ink-900">{client.name}</div>
                <code className="text-xs text-ink-400">{client.slug}</code>
              </Td>
              <Td>
                <Badge tone={client.status === 'active' ? 'good' : 'neutral'}>{client.status}</Badge>
              </Td>
              <Td className="tabular-nums">{client.contactCount.toLocaleString()}</Td>
              <Td className="tabular-nums">{client.activeContacts.toLocaleString()}</Td>
              <Td className="text-xs">{client.timezone}</Td>
              <Td className="max-w-sm text-xs text-ink-500">
                {client.physicalAddress ?? (
                  <span className="text-amber-700">Missing - campaigns will fail the compliance check</span>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}
